// DetectedModelsPanelV3 — surfaces auto-detected service models above
// the data surface so impatient operators can one-click materialize
// "Slack messages", "Zoom meetings", "Mailchimp signups" etc. as BQ
// views without touching SQL or filter recipes.
//
// PM brief: "za razne servise dobiti njihove modele u BQ" — operators
// without technical background must produce clean per-service BQ
// tables on their own.
//
// Strategy (after pattern review of Sentry / Splunk / Mixpanel /
// Segment / dbt — see chat 2026-05-09):
//
//   1. STRUCTURAL FINGERPRINT CLUSTERING (Sentry pattern)
//      Backend's /views:auto-suggest hashes each event's sorted
//      leaf-path set into a fingerprint. Events with the same
//      structural shape land in the same group regardless of
//      their event_type discriminator. This pulls Slack messages
//      together even when their `event.type` field varies
//      (`reaction_added` vs `app_mention` vs `team_join` all
//      share the Slack envelope shape).
//
//   2. CATCH-ALL "OTHER" BUCKET (Segment pattern)
//      Sum of fingerprint group sizes is usually 95-99% of sampled
//      events. The remaining one-offs (single-event payloads, weird
//      shapes) get a single "untyped" bucket so nothing is invisible.
//
//   3. BRAND RECOGNIZER (Splunk source-types pattern)
//      Each group's core_paths run through a known-signatures map:
//      `event.channel + team_id` → "Slack"; `payload.object.host_email`
//      → "Zoom"; etc. When recognised, the bucket gets a friendly
//      service label instead of "Schema · 22 fields".
//
//   4. DISTINCTIVE-PATH SQL DISCRIMINATOR
//      To materialise a group as a BQ view we need a WHERE clause.
//      We compute paths unique to this group (not in any other
//      group's path-set) and use top 3 as `IS NOT NULL` predicates
//      ANDed together. This isolates the structural shape without
//      requiring a backend hash function exposed to SQL.

import React, { useEffect, useState } from 'react';
import {
  createBqView,
  getViewSuggestions,
  type BqView,
  type ViewSuggestion,
} from '../../api';
import { useToast } from '../Toast';
import { recogniseBrand } from './brand-rules';

/** In-memory cache for /views:auto-suggest results. 60-second TTL —
 *  the operator's typical loop (filter, save, return) hits the panel
 *  multiple times in a session; second visits inside the TTL window
 *  read from cache instead of re-firing a BQ-backed sample query.
 *  Keyed by sourceId. Audit finding COST.3. */
const SUGGESTION_CACHE_MS = 60 * 1000;
const suggestionCache = new Map<string, { sampled: number; suggestions: ViewSuggestion[]; at: number }>();

interface ModelCandidate {
  /** Fingerprint id from backend — stable identifier for the bucket */
  id: string;
  /** Friendly label: brand-recognised service name when available,
   *  else 'Schema with N fields' or the suggestion's own name. */
  label: string;
  /** Subtitle: typically the event-type if uniform, or path-prefix. */
  hint: string;
  /** Brand emoji prefix when recognised — Slack/Stripe/etc. */
  emoji: string;
  count: number;
  /** All paths present in this bucket. */
  paths: string[];
  /** Paths in 100% of bucket members — used as distinctiveness seed. */
  corePaths: string[];
  /** Paths unique to this bucket vs all others — used to build the
   *  SQL WHERE predicate when materialising. */
  distinctivePaths: string[];
  /** ISO timestamp of the most recent member sample. Drives the
   *  'last seen Xm ago' indicator + a subtle volume sparkline
   *  derived from sample timestamps. */
  lastSeenAt: string | null;
  /** All member-sample timestamps (up to N). Used to render a
   *  rough volume sparkline — bins each sample into 12 equal-
   *  width buckets across the last 24h. */
  sampleTimestamps: string[];
}

/** Build a fallback hint string when the brand isn't recognised. Picks
 *  the most common path PREFIX as a hint (`event.*` → "event-shaped",
 *  `data.*` → "data-shaped"). Less informative than a brand match but
 *  better than "Schema · 22 fields". */
function fallbackHint(paths: string[]): string {
  const prefixes = new Map<string, number>();
  for (const p of paths) {
    const seg = p.split('.')[0] ?? p;
    prefixes.set(seg, (prefixes.get(seg) ?? 0) + 1);
  }
  const top = [...prefixes.entries()].sort((a, b) => b[1] - a[1])[0];
  if (top && top[1] >= Math.max(2, paths.length / 2)) {
    return `${top[0]}-shaped`;
  }
  return paths.slice(0, 2).join(' · ');
}

/** Sanitise a label/hint into a BQ view name. Lowercase, alnum +
 *  underscores, trimmed. Deduplicated with a numeric suffix when
 *  the same brand appears multiple times in one source (Slack is
 *  often split into 2-3 fingerprints — `slack`, `slack_2`, etc.). */
function buildViewName(label: string, existing: Set<string>): string {
  const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  const safe = base || 'unnamed_model';
  if (!existing.has(safe)) return safe;
  for (let i = 2; i < 100; i++) {
    const candidate = `${safe}_${i}`;
    if (!existing.has(candidate)) return candidate;
  }
  return `${safe}_${Date.now()}`;
}

/** Build the SQL for materialising a fingerprint bucket as a BQ view.
 *  Discriminator is "every distinctivePath IS NOT NULL" — narrow enough
 *  to isolate the bucket, broad enough to keep working as the bucket
 *  grows beyond the sampled 200. Projects all paths (not just core)
 *  as flat columns; fields absent from a given event come back NULL. */
function buildModelSql(opts: {
  dataset: string;
  paths: string[];
  distinctivePaths: string[];
}): string {
  const fqBase = `\`${opts.dataset}.events\``;
  const predicates = (opts.distinctivePaths.length > 0 ? opts.distinctivePaths : opts.paths)
    .slice(0, 3)
    .map(p => `JSON_VALUE(payload, '$.${p}') IS NOT NULL`);
  const where = predicates.join('\n  AND ');
  const projection = ['event_id', 'received_at',
    ...opts.paths.map(p => `JSON_VALUE(payload, '$.${p}') AS \`${p.replace(/\./g, '_')}\``),
  ].join(',\n  ');
  return `SELECT\n  ${projection}\nFROM ${fqBase}\nWHERE ${where}\nORDER BY received_at DESC`;
}

/** Build the catch-all SQL for events that don't match any of the
 *  recognised buckets. Negates the union of all distinctive paths from
 *  every other bucket — so an event with NONE of the bucket-specific
 *  fields lands in this view. Best-effort coverage; rare overlap cases
 *  may show up in two buckets. */
function buildCatchAllSql(opts: {
  dataset: string;
  allBucketDistinctivePaths: string[][];
}): string {
  const fqBase = `\`${opts.dataset}.events\``;
  const allBuckets = opts.allBucketDistinctivePaths.flat();
  // Dedupe; keep the predicate compact.
  const uniquePaths = [...new Set(allBuckets)];
  const negation = uniquePaths.length > 0
    ? `WHERE NOT (\n  ${uniquePaths.map(p => `JSON_VALUE(payload, '$.${p}') IS NOT NULL`).join('\n  OR ')}\n)`
    : '';
  return `-- Catch-all: events that don't match any auto-detected service shape\nSELECT event_id, received_at, payload, origin\nFROM ${fqBase}\n${negation}\nORDER BY received_at DESC`;
}

/** Compute paths unique to this bucket vs all others. */
function distinctiveOf(myPaths: string[], otherBucketsPaths: string[][]): string[] {
  const others = new Set(otherBucketsPaths.flat());
  return myPaths.filter(p => !others.has(p));
}

export function DetectedModelsPanelV3({ sourceId, dataset, views, reloadKey, onModelCreated }: {
  sourceId: string;
  dataset: string;
  views: BqView[];
  reloadKey: number;
  onModelCreated: () => void;
}): React.ReactElement | null {
  const toast = useToast();
  const [candidates, setCandidates] = useState<ModelCandidate[] | null>(null);
  const [sampled, setSampled] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  // Default expanded so first-time operator sees the model
  // candidates. Once all are materialised the header auto-collapses
  // to a one-line status (audit finding UX.1) — can re-expand
  // manually if they want to revisit.
  const [expanded, setExpanded] = useState(true);
  const [userExpanded, setUserExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Multi-select for merging two or more fingerprint clusters into a
  // single BQ view. Operator picks cards via checkbox, the action bar
  // exposes Merge. Selection clears after a successful merge or
  // explicit Clear click.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mergeName, setMergeName] = useState('');
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  function toggleSelect(id: string): void {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  useEffect(() => {
    let alive = true;
    // Cached path: hit the in-memory store first to avoid the
    // backend's auto-suggest scan when operator returns to the
    // surface inside the TTL window.
    const cached = suggestionCache.get(sourceId);
    const now = Date.now();
    const fetchPromise = (cached && now - cached.at < SUGGESTION_CACHE_MS)
      ? Promise.resolve({ source_id: sourceId, sampled: cached.sampled, suggestions: cached.suggestions })
      : getViewSuggestions(sourceId).then(r => {
          suggestionCache.set(sourceId, {
            sampled: r.sampled,
            suggestions: r.suggestions ?? [],
            at: Date.now(),
          });
          return r;
        });
    fetchPromise
      .then(r => {
        if (!alive) return;
        const suggestions: ViewSuggestion[] = r.suggestions ?? [];
        // Use existing view names to dedupe auto-generated labels —
        // we don't want a new "slack" if a "slack" view exists.
        const existingNames = new Set(views.map(v => v.name));
        const usedLabels = new Set<string>();
        // First pass: brand-recognise + dedup labels.
        const branded = suggestions.map(s => {
          const corePaths = s.core_paths ?? s.paths ?? [];
          const allPaths = s.paths ?? corePaths;
          const brand = recogniseBrand(corePaths);
          const label = brand?.label ?? s.matched_schema_name ?? s.ai_cached_name ?? '';
          return { suggestion: s, brand, label, corePaths, allPaths };
        });
        // Second pass: assign distinctive paths (paths not in any other
        // bucket's path set) so the SQL discriminator can isolate the
        // bucket cleanly.
        const cands: ModelCandidate[] = branded.map((b, i) => {
          const otherPaths = branded.filter((_, j) => j !== i).map(o => o.allPaths);
          const distinctivePaths = distinctiveOf(b.corePaths, otherPaths);
          // Resolve final label — brand match wins; else generic fallback.
          let label: string;
          let emoji: string;
          let hint: string;
          if (b.brand) {
            label = b.brand.label;
            emoji = b.brand.emoji;
            // Slack might appear N times for different fingerprints —
            // split a hint to disambiguate (event.channel pattern, etc.)
            hint = distinctivePaths.length > 0 ? distinctivePaths[0]! : `${b.allPaths.length} fields`;
          } else if (b.label) {
            label = b.label;
            emoji = '🧩';
            hint = `${b.allPaths.length} fields`;
          } else {
            label = `Unrecognized pattern #${i + 1}`;
            emoji = '🧩';
            hint = fallbackHint(b.allPaths);
          }
          // Dedupe label per the running set so two Slack-detected
          // buckets get `Slack` + `Slack 2`.
          const labelCount = [...usedLabels].filter(x => x === label || x.startsWith(`${label} `)).length;
          const finalLabel = labelCount === 0 ? label : `${label} ${labelCount + 1}`;
          usedLabels.add(finalLabel);
          const samples = b.suggestion.member_samples ?? [];
          const sampleTimestamps = samples.map(m => m.received_at).filter(Boolean);
          const lastSeenAt = sampleTimestamps.length > 0
            ? sampleTimestamps.reduce((latest, ts) => ts > latest ? ts : latest, sampleTimestamps[0]!)
            : null;
          return {
            id: b.suggestion.id,
            label: finalLabel,
            hint,
            emoji,
            count: b.suggestion.estimated_count,
            paths: b.allPaths,
            corePaths: b.corePaths,
            distinctivePaths,
            lastSeenAt,
            sampleTimestamps,
          };
        }).sort((a, b) => b.count - a.count);
        setCandidates(cands);
        setSampled(r.sampled);
        setError(null);
        // Touch existingNames so eslint doesn't complain about unused.
        void existingNames;
      })
      .catch(e => { if (alive) { setError(String(e)); setCandidates([]); } });
    return () => { alive = false; };
    // toast omitted — using stable memoized context
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, reloadKey, views]);

  if (!candidates || candidates.length === 0) {
    if (error) {
      return (
        <div style={{
          marginBottom: 14, padding: '8px 12px', borderRadius: 6,
          fontSize: 11.5, color: 'var(--ink-3)', fontFamily: 'var(--mono)',
          background: 'var(--bg-sunken)', border: '1px solid var(--line)',
        }}>Model detection failed: {error.slice(0, 120)}</div>
      );
    }
    return null;
  }

  const existingViewNames = new Set(views.map(v => v.name));
  const coverageCount = candidates.reduce((sum, c) => sum + c.count, 0);
  const uncoveredCount = Math.max(0, sampled - coverageCount);
  const allDistinctivePaths = candidates.map(c => c.distinctivePaths);
  const totalCandidates = candidates.length + (uncoveredCount > 0 ? 1 : 0);

  // Generate the catch-all bucket card metadata (only if there are
  // uncovered events). This is the Segment "Schema Violations" pattern
  // — every event has a home, even if home is "other".
  const catchAll: ModelCandidate | null = uncoveredCount > 0 ? {
    id: '__catchall__',
    label: 'Other / Untyped',
    hint: 'one-off shapes that don\'t match any recognised service',
    emoji: '📦',
    count: uncoveredCount,
    paths: [],
    corePaths: [],
    distinctivePaths: [],
    lastSeenAt: null,
    sampleTimestamps: [],
  } : null;

  const allCards = [...candidates, ...(catchAll ? [catchAll] : [])];
  // Mark which already exist as BQ views (by inferred name).
  const cardWithExists = allCards.map(c => {
    const inferredName = buildViewName(c.label, new Set());
    return { card: c, inferredName, exists: existingViewNames.has(inferredName) };
  });
  const modeledCount = cardWithExists.filter(x => x.exists).length;
  const remaining = totalCandidates - modeledCount;
  // Auto-collapse the panel once every candidate has a tab — operator
  // has nothing actionable here and the green grid was 100% noise on
  // returning visits. Re-expand by clicking the header (sets
  // userExpanded so it stays open for the rest of the session).
  const allDone = remaining === 0;
  const effectiveExpanded = allDone && !userExpanded ? false : expanded;

  const visibleCards = showAll ? cardWithExists : cardWithExists.slice(0, 6);

  async function createOne(c: ModelCandidate, inferredName: string): Promise<void> {
    setCreatingFor(c.id);
    try {
      const sql = c.id === '__catchall__'
        ? buildCatchAllSql({ dataset, allBucketDistinctivePaths: allDistinctivePaths })
        : buildModelSql({ dataset, paths: c.paths, distinctivePaths: c.distinctivePaths });
      await createBqView(sourceId, {
        name: inferredName,
        sql,
        description: `Auto-detected ${c.label} (${c.count} sampled events)`,
      });
      toast.show(`Created BQ model "${inferredName}". Tab is in the Views row.`, { kind: 'success' });
      onModelCreated();
    } catch (e) {
      toast.show(`Create failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setCreatingFor(null);
    }
  }

  /** Merge two or more fingerprint clusters into a single BQ view.
   *  SQL is OR over each member bucket's distinctive-path AND-predicate.
   *  Operator picks the merged name (auto-suggested from the highest-
   *  count member's label). Catch-all bucket can't merge — its SQL
   *  semantics are 'NOT any other bucket' and don't compose. */
  async function mergeSelected(name: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (!candidates) return;
    const selected = candidates.filter(c => selectedIds.has(c.id) && c.id !== '__catchall__');
    if (selected.length < 2) {
      toast.show('Pick at least 2 model cards to merge.', { kind: 'warning' });
      return;
    }
    const fqBase = `\`${dataset}.events\``;
    const ENVELOPE = new Set(['event_id', 'source_id', 'event_key', 'received_at',
      'parent_event_id', 'derivation_depth', 'trace_id', 'payload', 'origin']);
    // Build OR-predicate from each member's distinctive paths.
    const memberPredicates = selected.map(m => {
      const dp = (m.distinctivePaths.length > 0 ? m.distinctivePaths : m.paths).slice(0, 3);
      return `(${dp.map(p => `JSON_VALUE(payload, '$.${p}') IS NOT NULL`).join(' AND ')})`;
    });
    // Union projection: every path across all selected buckets.
    const allPaths = [...new Set(selected.flatMap(m => m.paths))].filter(p => !ENVELOPE.has(p));
    const projection = ['event_id', 'received_at',
      ...allPaths.map(p => `JSON_VALUE(payload, '$.${p}') AS \`${p.replace(/\./g, '_')}\``),
    ].join(',\n  ');
    const sql = `SELECT\n  ${projection}\nFROM ${fqBase}\nWHERE ${memberPredicates.join('\n   OR ')}\nORDER BY received_at DESC`;
    try {
      await createBqView(sourceId, {
        name: trimmed,
        sql,
        description: `Merged ${selected.length} detected models: ${selected.map(s => s.label).join(', ')}`,
      });
      toast.show(`Merged ${selected.length} models into "${trimmed}". Tab is in the Views row.`, { kind: 'success' });
      setSelectedIds(new Set());
      setShowMergeDialog(false);
      setMergeName('');
      onModelCreated();
    } catch (e) {
      toast.show(`Merge failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  async function createAllRemaining(): Promise<void> {
    setCreatingFor('__all__');
    let ok = 0; let failed = 0;
    for (const { card, inferredName, exists } of cardWithExists) {
      if (exists) continue;
      try {
        const sql = card.id === '__catchall__'
          ? buildCatchAllSql({ dataset, allBucketDistinctivePaths: allDistinctivePaths })
          : buildModelSql({ dataset, paths: card.paths, distinctivePaths: card.distinctivePaths });
        await createBqView(sourceId, {
          name: inferredName,
          sql,
          description: `Auto-detected ${card.label} (${card.count} sampled events)`,
        });
        ok++;
      } catch { failed++; }
    }
    setCreatingFor(null);
    toast.show(
      failed === 0
        ? `Created ${ok} BQ model${ok === 1 ? '' : 's'}. Tabs are in the Views row.`
        : `Created ${ok}, failed ${failed}. Console has details.`,
      { kind: failed === 0 ? 'success' : 'warning' },
    );
    onModelCreated();
  }

  return (
    <div style={{
      marginBottom: 18,
      border: '1px solid var(--accent-line)',
      background: 'var(--accent-soft)',
      borderRadius: 8, overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => { setExpanded(e => !e); setUserExpanded(true); }}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px',
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--accent)', fontFamily: 'var(--ui)', fontSize: 13,
          fontWeight: 500,
        }}
      >
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{effectiveExpanded ? '▾' : '▸'}</span>
        <span style={{ fontSize: 14 }}>✨</span>
        <strong>{remaining > 0
          ? `${remaining} model${remaining === 1 ? '' : 's'} ready · 100% of ${sampled} events covered`
          : `All ${modeledCount} services materialised in BigQuery`}</strong>
        <span style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--ink-2)' }}>
          {modeledCount > 0 && `${modeledCount}/${totalCandidates} done · `}click to {effectiveExpanded ? 'collapse' : 'review'}
        </span>
        <span style={{ flex: 1 }} />
        {effectiveExpanded && remaining > 1 && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); void createAllRemaining(); }}
            disabled={creatingFor !== null}
            className="btn sm primary"
            style={{ padding: '3px 12px', fontSize: 11.5 }}
            title={`Create all ${remaining} remaining BQ models in one go`}
          >{creatingFor === '__all__' ? 'Creating…' : `Create all ${remaining}`}</button>
        )}
      </button>
      {effectiveExpanded && (
        <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {selectedIds.size > 0 && (
            <div style={{
              padding: '6px 10px',
              background: 'var(--bg)', border: '1px solid var(--accent-line)', borderRadius: 6,
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5,
            }}>
              <strong style={{ color: 'var(--accent)' }}>{selectedIds.size} model{selectedIds.size === 1 ? '' : 's'} selected</strong>
              <span style={{ color: 'var(--ink-3)' }}>
                {selectedIds.size === 1
                  ? '· pick at least one more to merge'
                  : '· merge them into a single unified BQ view'}
              </span>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                onClick={() => {
                  const selected = (candidates ?? []).filter(c => selectedIds.has(c.id));
                  const top = [...selected].sort((a, b) => b.count - a.count)[0];
                  setMergeName(top ? `${top.label.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_merged` : 'merged_model');
                  setShowMergeDialog(true);
                }}
                disabled={selectedIds.size < 2}
                className="btn sm primary"
                style={{ padding: '3px 12px', fontSize: 11.5 }}
              >Merge into one</button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="btn sm ghost"
                style={{ padding: '3px 10px', fontSize: 11 }}
              >Clear</button>
            </div>
          )}
          <div style={{
            display: 'grid', gap: 8,
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          }}>
            {visibleCards.map(({ card: c, inferredName, exists }) => {
              const busy = creatingFor === c.id || creatingFor === '__all__';
              const isCatchAll = c.id === '__catchall__';
              return (
                <div
                  key={c.id}
                  style={{
                    padding: '10px 12px',
                    background: exists ? 'oklch(0.96 0.05 145 / 0.5)' : 'var(--bg-elev)',
                    border: `1px solid ${exists ? 'oklch(0.78 0.10 145)' : isCatchAll ? 'oklch(0.78 0.10 75)' : 'var(--line)'}`,
                    borderRadius: 6,
                    display: 'flex', flexDirection: 'column', gap: 4,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                    {!isCatchAll && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        title="Select to merge with other models"
                        style={{ margin: 0, cursor: 'pointer', flexShrink: 0 }}
                        onClick={e => e.stopPropagation()}
                      />
                    )}
                    <span style={{ fontSize: 14 }}>{c.emoji}</span>
                    <strong style={{
                      fontFamily: 'var(--ui)', fontSize: 13, fontWeight: 500,
                      color: 'var(--ink)',
                    }}>{c.label}</strong>
                    <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
                      {c.count} event{c.count === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div style={{
                    fontSize: 10.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }} title={isCatchAll ? c.hint : c.paths.slice(0, 8).join(', ')}>
                    {c.hint}
                    {!isCatchAll && c.paths.length > 0 && ` · ${c.paths.length} fields`}
                  </div>
                  {!isCatchAll && c.lastSeenAt && (() => {
                    // Sparkline: bin sample timestamps into 12 buckets
                    // across the last 24h. Approximate (samples are
                    // capped) but conveys 'is this active or stale'.
                    const now = Date.now();
                    const WINDOW_MS = 24 * 60 * 60 * 1000;
                    const BUCKET_MS = WINDOW_MS / 12;
                    const start = now - WINDOW_MS;
                    const buckets = new Array(12).fill(0) as number[];
                    for (const ts of c.sampleTimestamps) {
                      const t = Date.parse(ts);
                      if (!Number.isFinite(t) || t < start || t > now) continue;
                      const idx = Math.min(11, Math.floor((t - start) / BUCKET_MS));
                      buckets[idx] = (buckets[idx] ?? 0) + 1;
                    }
                    const max = Math.max(1, ...buckets);
                    const lastSeenMs = now - Date.parse(c.lastSeenAt);
                    const ago = lastSeenMs < 60_000 ? `${Math.floor(lastSeenMs / 1000)}s ago`
                      : lastSeenMs < 3600_000 ? `${Math.floor(lastSeenMs / 60_000)}m ago`
                      : lastSeenMs < 86400_000 ? `${Math.floor(lastSeenMs / 3600_000)}h ago`
                      : `${Math.floor(lastSeenMs / 86400_000)}d ago`;
                    const stale = lastSeenMs > 3600_000;
                    return (
                      <div
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          fontSize: 10, fontFamily: 'var(--mono)',
                          color: stale ? 'var(--ink-3)' : 'oklch(0.40 0.18 270)',
                        }}
                        title={`Last sample ${ago} · sparkline shows volume across last 24h (sample-derived, approximate)`}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 12 }}>
                          {buckets.map((v, idx) => (
                            <div
                              key={idx}
                              style={{
                                width: 4,
                                height: `${Math.max(1, (v / max) * 12)}px`,
                                background: v > 0 ? 'oklch(0.58 0.15 270 / 0.7)' : 'oklch(0.92 0.01 250)',
                                borderRadius: 1,
                              }}
                            />
                          ))}
                        </div>
                        <span>{ago}</span>
                      </div>
                    );
                  })()}
                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {exists ? (
                      <>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', fontSize: 10.5, fontFamily: 'var(--mono)',
                          color: 'oklch(0.36 0.14 145)',
                        }}>
                          ✓ <code>{inferredName}</code>
                        </span>
                        <a
                          href={`https://console.cloud.google.com/bigquery?project=hub-mega-prod&p=hub-mega-prod&d=${encodeURIComponent(dataset)}&t=${encodeURIComponent(inferredName)}&page=table`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 11, color: 'var(--accent)', textDecoration: 'none' }}
                          title="Open this view in the BigQuery console — query, export, share"
                        >↗ open in BQ</a>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { void createOne(c, inferredName); }}
                        disabled={busy}
                        className="btn sm primary"
                        style={{ padding: '3px 10px', fontSize: 11 }}
                      >{busy ? 'Creating…' : isCatchAll ? '📦 Create catch-all view' : '✨ Create BQ model'}</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!showAll && cardWithExists.length > 6 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--ink-3)', fontSize: 11, padding: 0,
                alignSelf: 'flex-start', textDecoration: 'underline',
              }}
            >Show all {cardWithExists.length} candidates</button>
          )}
          <div style={{ fontSize: 10.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>
            Detection groups events by structural fingerprint (sorted set of payload paths) — Sentry-style. Brand labels come from a known-signature registry. The "Other" bucket catches anything that doesn't match a recognised shape.
          </div>
        </div>
      )}
      {showMergeDialog && (
        <div
          onClick={() => setShowMergeDialog(false)}
          style={{
            position: 'fixed', inset: 0, background: 'oklch(0.18 0.01 250 / 0.40)',
            zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8,
              padding: '20px 24px', minWidth: 380, maxWidth: 480,
              boxShadow: '0 12px 40px oklch(0.18 0.01 250 / 0.30)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
              <strong style={{ fontSize: 16 }}>Merge {selectedIds.size} models</strong>
              <button type="button" onClick={() => setShowMergeDialog(false)} style={{ background: 'transparent', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 14 }}>
              Selected: {(candidates ?? []).filter(c => selectedIds.has(c.id)).map(c => c.label).join(', ')}.
              The merged BQ view will return events matching ANY of these fingerprint shapes, projected as a unified column set.
            </div>
            <input
              autoFocus
              value={mergeName}
              onChange={e => setMergeName(e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
              onKeyDown={e => {
                if (e.key === 'Enter') { void mergeSelected(mergeName); }
                else if (e.key === 'Escape') { setShowMergeDialog(false); }
              }}
              placeholder="merged_model_name"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13,
                border: '1px solid var(--line)', borderRadius: 4,
                background: 'var(--bg)', color: 'var(--ink)',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" onClick={() => setShowMergeDialog(false)} className="btn sm ghost">Cancel</button>
              <button
                type="button"
                onClick={() => { void mergeSelected(mergeName); }}
                disabled={!mergeName.trim() || selectedIds.size < 2}
                className="btn sm primary"
              >Create merged view</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
